/**
 * Página do dia — dois modos (ver e reorganizar), quatro estados de dados.
 *
 * Duas regras congeladas do PRD vivem aqui, e ambas são resolvidas em
 * `@nv/core` para rodarem idênticas no servidor:
 *
 *  - reordenar muda a ordem, NUNCA o horário;
 *  - concluir pelo ✓ e pelo botão do detalhe produzem o mesmo efeito.
 */
import {
  type Activity,
  CHRONOLOGICAL_WARNING,
  calendarDate,
  DAY_CATEGORY_LABELS,
  dayColorOf,
  featureEnabled,
  formatDayMonth,
  isOutOfChronologicalOrder,
  tripDayNumber,
  weekdayName,
} from '@nv/core';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useDemo } from '../../demo/DemoContext';
import { EnquetesDoDia } from '../enquetes/EnquetesDoDia';
import { useAniversariosDaViagem } from '../aniversarios/api';
import { rotaNegado } from '../grupo/permissaoLabels';
import { usePermissao, useTrip } from '../../trip/TripContext';
import { tripInputDaViagem } from '../../trip/tripInput';
import { Icon } from '../../ui/icons';
import { Ilustracao } from '../../ui/Ilustracao';
import { EmptyState, ErrorState, Skeleton } from '../../ui/primitives';
import { useDiasDaViagem } from '../../trip/dias';
import { useCarregarDia } from './carregarDia';
import { EditarDiaSheet } from './EditarDiaSheet';
import { useItinerary } from './store';

const STATUS_LABEL: Record<Activity['status'], string> = {
  planejada: 'Planejada',
  agora: 'Agora',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
};

export function DiaScreen() {
  const { dia = '' } = useParams();
  const navigate = useNavigate();
  const { dataState } = useDemo();
  // Permissão de verdade, do servidor. Antes vinha de um seletor de papel do
  // painel de demonstração — que escondia botões sem mudar autorização nenhuma.
  const canReorder = usePermissao('reorder_itinerary');
  const canEditDay = usePermissao('edit_itinerary');
  const { nomes, trip, members, tripId } = useTrip();
  const diasDaViagem = useDiasDaViagem(tripId);
  const diaAtual = diasDaViagem.days?.find((d) => d.date === dia) ?? null;
  const [editandoDia, setEditandoDia] = useState(false);
  const doDia = useCarregarDia(dia);
  const aniversarios = useAniversariosDaViagem(tripId, featureEnabled(trip.features, 'aniversarios'));
  const aniversariantes = (aniversarios.data?.birthdays ?? []).filter((b) => b.date === dia).map((b) => b.name);

  // Estado no store, não local: é o que faz o ✓ daqui e o botão do detalhe
  // serem literalmente a mesma operação.
  const activities = useItinerary((s) => s.activities);
  const toggleCompletion = useItinerary((s) => s.toggleCompletion);
  const reorderInStore = useItinerary((s) => s.reorder);
  const ultimaAlteracao = useItinerary((s) => s.history[0] ?? null);

  const [reordering, setReordering] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);

  const ordenadas = useMemo(
    () => [...activities].sort((a, b) => a.sortOrder - b.sortOrder),
    [activities],
  );
  const foraDeOrdem = isOutOfChronologicalOrder(ordenadas);
  // Cancelada não conta: o dia está completo quando tudo o que vale foi feito.
  const ativas = ordenadas.filter((a) => a.status !== 'cancelada');
  const feitas = ativas.filter((a) => a.status === 'concluida').length;

  const soltar = (indice: number) => {
    if (!picked) return;
    reorderInStore(picked, indice);
    setPicked(null);
  };

  const dataCivil = (() => {
    try {
      return calendarDate(dia);
    } catch {
      return null;
    }
  })();

  // "DIA 3" vem das datas da viagem, na timezone dela — nunca de contagem local.
  const viagem = tripInputDaViagem(trip, members.length);
  const numeroDoDia = (() => {
    if (!viagem || !dataCivil) return null;
    try {
      return tripDayNumber(viagem, dataCivil);
    } catch {
      return null;
    }
  })();

  // O painel de demonstração ainda força estados de tela; a origem normal é o
  // carregamento real. Nenhum dos dois inventa atividade.
  const carregando = dataState === 'loading' || doDia.carregando;
  const comErro = dataState === 'error' || doDia.erro;
  const vazio = dataState === 'empty' || ordenadas.length === 0;

  return (
    <section className="screen dia">
      {/* Refino final: as ações no alto, à direita; embaixo, o tema e a data
          numa linha e o título do dia em 30/900, com a largura toda. */}
      <header className="dia-header dia-header--dia">
        <div className="dia-header__titles">
          <span className="dia-header__eyebrow">
            {diaAtual?.category && (
              <span className={`dia-tema dia-tema--${dayColorOf(diaAtual.category, diaAtual.color ?? null) ?? 'cinza'}`} data-testid="dia-tema">
                {DAY_CATEGORY_LABELS[diaAtual.category].rotulo}
                {diaAtual.destination ? ` · ${diaAtual.destination}` : ''}
              </span>
            )}
            <span className="dia-header__date">
              {dataCivil
                ? `${formatDayMonth(dataCivil)} · ${weekdayName(dataCivil)}${
                    numeroDoDia ? ` · dia ${numeroDoDia}` : ''
                  }`
                : dia}
            </span>
          </span>
          <h1 className="dia-header__title">
            {doDia.titulo ?? (numeroDoDia ? `Dia ${numeroDoDia}` : 'Dia')}
          </h1>
          {aniversariantes.length > 0 && (
            <span className="rot-row__aniv" data-testid="dia-aniversario">
              <Icon name="bolo" size={14} /> aniversário de {aniversariantes.join(' e ')}
            </span>
          )}
        </div>

        <div className="dia-header__acoes">
        {canEditDay && diaAtual && (
          <button type="button" className="nv-btn nv-btn--chip dia-reorder" onClick={() => setEditandoDia(true)} data-testid="editar-dia-abrir" aria-label="Editar dia">
            <Icon name="editar" size={18} />
          </button>
        )}
        {canReorder ? (
          <button
            type="button"
            className="nv-btn nv-btn--chip dia-reorder"
            onClick={() => {
              setReordering((v) => !v);
              setPicked(null);
            }}
          >
            <Icon name={reordering ? 'concluir' : 'reordenar'} size={18} />
            {reordering ? 'Concluir' : 'Reorganizar'}
          </button>
        ) : (
          // Ação que depende de liberação: aparece com cadeado e leva ao motivo.
          <button
            type="button"
            className="nv-btn nv-btn--chip dia-reorder dia-reorder--locked"
            onClick={() => navigate(rotaNegado('reorder_itinerary'))}
          >
            <Icon name="semPermissao" size={18} />
            Reorganizar
          </button>
        )}
        </div>
      </header>

      {carregando && (
        <>
          <Skeleton height={6} raio="chip" />
          <Skeleton height={240} />
        </>
      )}

      {!carregando && comErro && (
        <ErrorState
          title="Não foi possível carregar o dia"
          description="Mostrando a última versão salva no telefone. Alterações feitas agora sincronizam depois."
          onRetry={() => window.location.reload()}
        />
      )}

      {!carregando && !comErro && vazio && (
        <EmptyState
          ilustracao="atividade"
          title="Nenhuma atividade neste dia"
          description="Adicione a primeira — todo mundo do grupo vê na hora."
          action={{ label: 'Nova atividade', onClick: () => navigate(`/roteiro/${dia}/nova`) }}
        />
      )}

      {!carregando && !comErro && !vazio && (
        <>
          {foraDeOrdem && (
            <p className="dia-warning" role="status">
              <span className="dia-warning__icon">
                <Icon name="aviso" size={18} />
              </span>
              {CHRONOLOGICAL_WARNING}
            </p>
          )}

          {/* A barra do dia anda junto com o ✓; o dia inteiro feito ganha selo. */}
          {!reordering && ativas.length > 0 && (
            <div className="dia-progresso" data-testid="dia-progresso">
              <span className="dia-progresso__trilho" aria-hidden="true">
                <span className="dia-progresso__barra" style={{ width: `${Math.round((feitas / ativas.length) * 100)}%` }} />
              </span>
              <span className="dia-progresso__conta">
                {feitas} de {ativas.length}
              </span>
            </div>
          )}
          {!reordering && ativas.length > 0 && feitas === ativas.length && (
            <div className="dia-completo nv-pop" role="status" data-testid="dia-completo">
              <Ilustracao nome="concluido" tamanho={52} />
              <span className="dia-completo__texto">
                <b>Dia completo!</b>
                <span>{ativas.length === 1 ? 'A atividade do dia está feita.' : `Todas as ${ativas.length} atividades feitas.`}</span>
              </span>
            </div>
          )}

          <ol className="dia-list">
            {reordering && picked && (
              <li>
                <DropSlot onDrop={() => soltar(0)} />
              </li>
            )}

            {ordenadas.map((activity, indice) => (
              <li key={activity.id}>
                <div
                  className={[
                    'dia-item',
                    activity.status === 'agora' ? 'dia-item--now' : '',
                    activity.status === 'concluida' ? 'dia-item--done' : '',
                    activity.status === 'cancelada' ? 'dia-item--cancelled' : '',
                    picked === activity.id ? 'dia-item--picked' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span className="dia-item__time">
                    {activity.allDay ? (
                      <span className="dia-item__time-inteiro">Dia inteiro</span>
                    ) : (
                      <>
                        {activity.startTime}
                        {activity.endTime && <span className="dia-item__time-fim">até {activity.endTime}</span>}
                      </>
                    )}
                  </span>
                  {activity.coverThumbUrl && <img className="dia-item__capa" src={activity.coverThumbUrl} alt="" loading="lazy" data-testid="dia-capa" />}

                  <button
                    type="button"
                    className="dia-item__body"
                    onClick={() => navigate(`/roteiro/${dia}/${activity.id}`)}
                    disabled={reordering}
                  >
                    <span className="dia-item__title">{activity.title}</span>
                    {picked === activity.id ? (
                      <span className="dia-item__picked-hint">Pego · escolha onde soltar</span>
                    ) : (
                      activity.locationName && (
                        <span className="dia-item__place">{activity.locationName}</span>
                      )
                    )}
                  </button>

                  {/* Status sempre em texto — para o leitor de tela e para quem
                      não distingue cor. Como chip visível só quando não é o ✓
                      que já diz: "agora" e "cancelada". */}
                  <span
                    className={
                      activity.status === 'agora' || activity.status === 'cancelada'
                        ? `dia-status dia-status--${activity.status}`
                        : 'dia-status nv-visually-hidden'
                    }
                  >
                    {STATUS_LABEL[activity.status]}
                  </span>

                  {reordering ? (
                    <button
                      type="button"
                      className="dia-item__grab"
                      aria-label={
                        picked === activity.id
                          ? `Soltar ${activity.title}`
                          : `Pegar ${activity.title} para reordenar`
                      }
                      aria-pressed={picked === activity.id}
                      onClick={() => setPicked(picked === activity.id ? null : activity.id)}
                    >
                      <Icon name="reordenar" size={18} />
                    </button>
                  ) : (
                    activity.status !== 'cancelada' && (
                      <button
                        type="button"
                        className={
                          activity.status === 'concluida'
                            ? 'dia-item__check dia-item__check--done'
                            : 'dia-item__check'
                        }
                        aria-label={
                          activity.status === 'concluida'
                            ? `Reabrir ${activity.title}`
                            : `Concluir ${activity.title}`
                        }
                        aria-pressed={activity.status === 'concluida'}
                        onClick={() => toggleCompletion(activity.id)}
                      >
                        <span className="nv-check" aria-hidden="true">
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path d="m5 12.5 4.5 4.5L19 7.5" />
                          </svg>
                        </span>
                      </button>
                    )
                  )}
                </div>

                {reordering && picked && picked !== activity.id && (
                  <DropSlot onDrop={() => soltar(indice + 1)} />
                )}
              </li>
            ))}
          </ol>

          {reordering ? (
            <>
              <p className="dia-nota">
                Mover muda a ordem — <b>o horário vai junto com a atividade</b>. Para
                trocar o horário, edite a atividade.
              </p>
              <button
                type="button"
                className="nv-btn nv-btn--primary nv-btn--block"
                onClick={() => {
                  setReordering(false);
                  setPicked(null);
                }}
              >
                Concluir reorganização
              </button>
            </>
          ) : (
            <>
              {/* Quem alterou por último sai do histórico real deste dia. Sem
                  alteração registrada, a linha não aparece. */}
              {ultimaAlteracao && (
                <p className="dia-footer">
                  Alterado por {nomes[ultimaAlteracao.authorId] ?? 'alguém do grupo'} ·{' '}
                  <button
                    type="button"
                    className="dia-footer__link"
                    onClick={() => navigate(`/roteiro/${dia}/historico`)}
                  >
                    ver alterações
                  </button>
                </p>
              )}

              <button
                type="button"
                className="nv-btn nv-btn--primary nv-btn--block"
                onClick={() => navigate(`/roteiro/${dia}/nova`)}
              >
                <Icon name="criar" size={20} />
                Nova atividade
              </button>
            </>
          )}
        </>
      )}

      {/* A enquete do dia vive aqui, com ou sem atividade — "onde vamos
          jantar?" é pergunta de dia vazio também. Some ao reorganizar. */}
      {!carregando && !reordering && <EnquetesDoDia dayId={doDia.dayId} />}
      {editandoDia && diaAtual && <EditarDiaSheet tripId={tripId} dia={diaAtual} onFechar={() => setEditandoDia(false)} />}
    </section>
  );
}

/** Zona "SOLTAR AQUI" — alternativa acessível ao arraste (PRD §23). */
function DropSlot({ onDrop }: { onDrop: () => void }) {
  return (
    <button type="button" className="dia-slot" onClick={onDrop}>
      SOLTAR AQUI
    </button>
  );
}
